import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { assessCircling } from '../../api/client';
import Cesium3DPreviewModal from '../../components/Cesium3DPreviewModal';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

function hexToRgba(hex, opacity) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!m) return `rgba(255,255,255,${opacity})`;
    const [r, g, b] = m.slice(1).map(h => parseInt(h, 16));
    return `rgba(${r},${g},${b},${opacity})`;
}

export default function CirclingPage({ project, arps, obstacles, loading, state, setState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const { getStyle } = useSurfaceStyles();
    const catColors = useMemo(() => {
        const out = {};
        ['a', 'b', 'c', 'd', 'e'].forEach(k => {
            const s = getStyle(`circling.${k}`);
            out[k.toUpperCase()] = { fill: hexToRgba(s.color, s.opacity), border: s.color };
        });
        return out;
    }, [getStyle]);
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate a circling category, not just its tallest point overall.
    const cirResultsForShield = state?.cirResults || {};
    const penetratingObstacleIds = useMemo(() => {
        const ids = [];
        Object.values(cirResultsForShield).forEach(entry => {
            (entry?.data?.results || []).forEach(r => { if (r.penetrates) ids.push(r.obstacle_id); });
        });
        return ids;
    }, [cirResultsForShield]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);

    // ── Circling shielding — real per-category check, not just flat elevation ──
    // Circling categories aren't sloped, but they ARE zoned: category A/B/C/D
    // each has its own minima, so a candidate sitting in a stricter category
    // at a modest elevation can genuinely penetrate while the project's own
    // (taller) obstacle sits in a more lenient category — a flat elevation
    // comparison misses that the same way it missed IHS (see OLSPage.jsx).
    // "True" penetration amount = total_elev_ft - category_minima_ft (matches
    // the recomputation already done for the results table above, since the
    // backend zeroes penetration_ft/_m for non-penetrating evaluations).
    const circlingImpacts = useMemo(() => {
        const out = {}; // `${icao}_${category}` -> worst (highest) trueP
        Object.entries(cirResultsForShield).forEach(([icao, entry]) => {
            (entry?.data?.results || []).forEach(r => {
                (r.evaluations || []).forEach(ev => {
                    const trueP = ev.total_elev_ft - ev.category_minima_ft;
                    if (trueP <= 0) return;
                    const key = `${icao}_${ev.category}`;
                    if (!(key in out) || trueP > out[key]) out[key] = trueP;
                });
            });
        });
        return out;
    }, [cirResultsForShield]);
    const hasCirclingImpact = Object.keys(circlingImpacts).length > 0;

    const [circlingShieldPens, setCirclingShieldPens] = useState({}); // icao -> candidate result rows
    useEffect(() => {
        if (!shieldOn || !shieldData || !hasCirclingImpact) { setCirclingShieldPens({}); return; }
        const icaos = [...new Set(Object.keys(circlingImpacts).map(k => k.split('_')[0]))];
        if (!icaos.length) return;
        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setCirclingShieldPens({}); return; }
        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));
        let cancelled = false;
        (async () => {
            const perIcao = {};
            for (const icao of icaos) {
                try {
                    const { data } = await assessCircling(icao, pseudoObstacles);
                    perIcao[icao] = data.results || [];
                } catch (e) {
                    console.error(`Circling shield check failed for ${icao}`, e);
                }
            }
            if (!cancelled) setCirclingShieldPens(perIcao);
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasCirclingImpact, circlingImpacts]);

    // Candidate pseudo-id -> [{ icao, category, trueP, projectTrueP }, ...] —
    // beats the project's own worst on that category (the amber "true
    // shield" check).
    const circlingShieldVerdicts = useMemo(() => {
        const out = new Map();
        Object.entries(circlingShieldPens).forEach(([icao, rows]) => {
            rows.forEach(r => {
                (r.evaluations || []).forEach(ev => {
                    const trueP = ev.total_elev_ft - ev.category_minima_ft;
                    if (trueP <= 0) return;
                    const key = `${icao}_${ev.category}`;
                    const projectTrueP = circlingImpacts[key];
                    if (projectTrueP == null) return;
                    if (trueP >= projectTrueP) {
                        const list = out.get(r.obstacle_id) || [];
                        list.push({ icao, category: ev.category, trueP, projectTrueP });
                        out.set(r.obstacle_id, list);
                    }
                });
            });
        });
        return out;
    }, [circlingShieldPens, circlingImpacts]);

    const circlingShieldInsideSurface = useMemo(() => {
        const out = new Map();
        Object.values(circlingShieldPens).forEach(rows => {
            rows.forEach(r => { if ((r.evaluations || []).length) out.set(r.obstacle_id, true); });
        });
        return out;
    }, [circlingShieldPens]);

    // Candidate pseudo-id -> [{ icao, category, margin }, ...] for every
    // category it genuinely penetrates at its own position (trueP > 0),
    // regardless of raw elevation or of beating the project's worst — see
    // olsShieldRealPenetration in OLSPage.jsx.
    const circlingShieldRealPenetration = useMemo(() => {
        const out = new Map();
        Object.values(circlingShieldPens).forEach(rows => {
            rows.forEach(r => {
                (r.evaluations || []).forEach(ev => {
                    const trueP = ev.total_elev_ft - ev.category_minima_ft;
                    if (trueP <= 0) return;
                    const list = out.get(r.obstacle_id) || [];
                    list.push({ category: ev.category, margin: trueP });
                    out.set(r.obstacle_id, list);
                });
            });
        });
        return out;
    }, [circlingShieldPens]);

    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real category
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasCirclingImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = circlingShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = circlingShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = circlingShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v => `Circling Cat ${v.category} (+${v.margin.toFixed(0)} ft)`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">Circling Cat ${v.category}</b> — this obstacle exceeds minima by ${v.trueP.toFixed(0)} ft (project worst: ${v.projectTrueP.toFixed(0)} ft)`
                  ).join('')
                : `<br/><span style="color:#8a94a6">Circling: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, circlingShieldVerdicts, circlingShieldInsideSurface, circlingShieldRealPenetration, hasCirclingImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield a
    // circling category — same pattern as OLSPage.jsx.
    const circlingTrueShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (circlingTrueShieldLayerRef.current) { mapInst.current.removeLayer(circlingTrueShieldLayerRef.current); circlingTrueShieldLayerRef.current = null; }
        if (!shieldOn || circlingShieldVerdicts.size === 0) return;
        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });
        circlingShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const lines = verdicts.map(v =>
                `<b>Circling Cat ${v.category}</b> (${v.icao}) — exceeds minima by <b>${v.trueP.toFixed(0)} ft</b> (project worst: ${v.projectTrueP.toFixed(0)} ft)`
            ).join('<br/>');
            marker.bindPopup(
                `<b>🛬 TRUE SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact ${verdicts.length > 1 ? 'on these categories' : 'on this category'}.</span>`
            );
            group.addLayer(marker);
        });
        if (group.getLayers().length > 0) {
            circlingTrueShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, circlingShieldVerdicts, shieldData]);
    const tileLyr = useRef(null);
    const mkrLyrs = useRef([]);
    const polyLyr = useRef(null);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [exportingAixm, setExportingAixm] = useState(false);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(220, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);
    
    // Helpers to update complex state prop
    const setSelId = useCallback((id) => setState(s => ({ ...s, selObstId: id })), [setState]);
    const setVisible = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.visible) : v;
        return { ...s, visible: next };
    }), [setState]);
    const setCatVisible = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.catVisible) : v;
        return { ...s, catVisible: next };
    }), [setState]);
    const setActiveIcao = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.activeIcao) : v;
        return { ...s, activeIcao: next };
    }), [setState]);
    const setCirResults = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.cirResults) : v;
        return { ...s, cirResults: next };
    }), [setState]);
    const setCapturedCircling = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.capturedCircling) : v;
        return { ...s, capturedCircling: next };
    }), [setState]);

    const { selObstId, visible, catVisible, activeIcao, cirResults, capturedCircling } = state;
    const lastFittedObsId = useRef(null);

    const obsWithCoords = obstacles.filter(o => o.latitude && o.longitude);
    const selectedObs = obsWithCoords.find(o => o.id === selObstId) || obsWithCoords[0];

    const [validIcaos, setValidIcaos] = useState([]);

    useEffect(() => {
        fetch(`${API_BASE}/circling/valid-aerodromes`)
            .then(res => res.json())
            .then(data => setValidIcaos(data || []))
            .catch(err => console.error("Failed to fetch valid circling aerodromes", err));
    }, []);

    // Filter relevant aerodromes
    const validArps = React.useMemo(() => {
        return arps.filter(a => (a.distance_km == null || a.distance_km <= 10.0));
    }, [arps]);

    useEffect(() => {
        if (!validArps.length) return;
        const vis = {};
        validArps.forEach(a => { vis[a.icao] = true; });
        setVisible(vis);
    }, [validArps, setVisible]);

    // Map init
    useEffect(() => {
        if (mapInst.current || !mapRef.current) return;
        mapInst.current = L.map(mapRef.current, {
            center: [-28.5, 25.0],
            zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7
        });
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        setTimeout(() => { mapInst.current?.invalidateSize(); }, 0);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
    }, []);

    // Toggle base layers
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileLyr.current) mapInst.current.removeLayer(tileLyr.current);
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // Render features
    useEffect(() => {
        if (!mapInst.current || loading) return;
        const map = mapInst.current;
        mkrLyrs.current.forEach(layer => map.removeLayer(layer));
        mkrLyrs.current = [];
        if (polyLyr.current) map.removeLayer(polyLyr.current);

        const bounds = [];
        const geoJSONs = [];

        validArps.forEach(arp => {
            if (visible[arp.icao] === false) return;
            const resData = cirResults[arp.icao]?.data;
            
            // Draw ARP
            if (arp.lat && arp.lon) {
                const pt = [arp.lat, arp.lon];
                bounds.push(pt);
                const m = L.marker(pt, { icon: getArpIcon(L, arp, showArpLabels) })
                    .bindTooltip(`<b>${arp.icao}</b><br/>ARP Elev: ${arp.elev_ft ? arp.elev_ft + ' ft' : 'N/A'}`, { direction: 'top' });
                if (onAerodromeContextMenu) {
                    m.on('contextmenu', e => {
                        e.originalEvent.preventDefault();
                        onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                    });
                }
                m.addTo(map);
                mkrLyrs.current.push(m);
            }

            // Collect GeoJSON Polygons
            if (resData && resData.polygons) {
                const mapFeats = resData.polygons.features.filter(f => {
                    return catVisible[arp.icao]?.[f.properties.category] !== false;
                });
                if (mapFeats.length > 0) {
                    geoJSONs.push({ ...resData.polygons, features: mapFeats });
                }
            }
        });

        if (geoJSONs.length > 0) {
            polyLyr.current = L.geoJSON(geoJSONs, {
                style: (feature) => {
                    const cat = feature.properties.category;
                    const c = catColors[cat];
                    return {
                        fillColor: c ? c.fill : '#fff',
                        fillOpacity: 1, // fill transparency handled in rgba string
                        color: c ? c.border : '#888',
                        weight: 1.5,
                        dashArray: '4,4'
                    };
                },
                onEachFeature: (f, layer) => {
                    layer.bindTooltip(`
                        <b>${f.properties.name}</b><br/>
                        Radius: ${f.properties.rthr_m} m<br/>
                        Minima: ${f.properties.minima_ft} ft AMSL
                    `);
                }
            }).addTo(map);
            bounds.push(polyLyr.current.getBounds());
        }

        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude]; bounds.push(ll);
            const isSel = o.id === selectedObs?.id;

            let isPenetrating = false;
            let isAssessed = false;
            let maxPen = -Infinity;
            let foundAny = false;
            let surfaceStr = '—';

            for (const arp of validArps) {
                if (visible[arp.icao] === false) continue;
                if (cirResults[arp.icao] && !cirResults[arp.icao].loading) isAssessed = true;
                const resData = cirResults[arp.icao]?.data;
                if (resData && resData.results) {
                    const obstacleResult = resData.results.find(r => r.obstacle_id === o.id);
                    if (obstacleResult) {
                        if (obstacleResult.penetrates) isPenetrating = true;
                        if (obstacleResult.evaluations) {
                            obstacleResult.evaluations.forEach(ev => {
                                foundAny = true;
                                const pen = ev.penetration_m ?? ((ev.total_elev_ft - ev.category_minima_ft) * 0.3048);
                                if (pen > maxPen) {
                                    maxPen = pen;
                                    surfaceStr = `Circling Category ${ev.category}`;
                                }
                            });
                        }
                    }
                }
            }

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const obsIconUrl = isPenetrating ? '/symbols/obstacle_red.svg' : '/symbols/obstacle_green.svg';
            const sz = isSel ? 80 : 65; const h = Math.round(sz / 2);
            const icon = getObstacleIcon(L, o, isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined, showObsLabels);
            const m = L.marker(ll, { icon, zIndexOffset: isSel ? 100 : 0 })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`)
                .on('click', () => setSelId(o.id));
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(map);
            mkrLyrs.current.push(m);
        });
        addHullPolygonsToMap(map, obsWithCoords, project?.id, mkrLyrs);

        if (bounds.length > 0) {
            try {
                // Leaflet bounds accept instances of LatLngBounds or Arrays
                let finalBounds = L.latLngBounds([]);
                bounds.forEach(b => {
                    if (b instanceof L.LatLngBounds) finalBounds.extend(b);
                    else finalBounds.extend(L.latLng(b));
                });
                if (finalBounds.isValid()) {
                    if (lastFittedObsId.current !== selectedObs?.id) {
                        map.fitBounds(finalBounds, { padding: [40, 40], maxZoom: 10 });
                        lastFittedObsId.current = selectedObs?.id;
                    }
                }
            } catch (e) { console.warn("Bounds error:", e); }
        }
    }, [validArps, obstacles, loading, cirResults, visible, catVisible, showObstacles, showObsLabels, showArpLabels, selObstId, onObstacleContextMenu, onAerodromeContextMenu, catColors]);


    const captureCirclingScreenshot = React.useCallback(async (icao) => {
        if (!mapRef.current) return;
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = '';
                clone.style.left = '0';
                clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }
            
            // Draw div-icon labels
            const divIconEls = [...container.querySelectorAll('.leaflet-marker-icon div')];
            for (const el of divIconEls) {
                if (el.querySelector('div')) continue;
                const eRect = el.getBoundingClientRect();
                const x = Math.round(eRect.left - containerRect.left);
                const y = Math.round(eRect.top - containerRect.top);
                const text = el.textContent?.trim();
                if (text) {
                    ctx.save();
                    ctx.font = 'bold 11px sans-serif';
                    ctx.fillStyle = el.style.color || '#ff9800';
                    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
                    ctx.lineWidth = 3;
                    ctx.strokeText(text, x, y + 10);
                    ctx.fillText(text, x, y + 10);
                    ctx.restore();
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const { default: api } = await import('../../api/client');
            await api.post('/circling/screenshot', { icao: icao.toUpperCase(), image: dataUrl, project_id: project?.id });
            setCapturedCircling(prev => ({ ...prev, [icao]: true }));
        } catch (err) {
            console.error('Failed to capture Circling screenshot:', err);
            alert("Error capturing screenshot: " + (err.message || String(err)));
        }
    }, [setCapturedCircling]);

    const clearCirclingScreenshot = React.useCallback(async (icao) => {
        try {
            const { default: api } = await import('../../api/client');
            await api.delete(`/circling/screenshot/${icao.toUpperCase()}`, { params: { project_id: project?.id } });
            setCapturedCircling(prev => {
                const next = { ...prev };
                delete next[icao];
                return next;
            });
        } catch (err) { console.error('Clear Circling screenshot error:', err); }
    }, []);

    const runAssessment = async (icao) => {
        try {
            setCirResults(p => ({ ...p, [icao]: { loading: true } }));
            setVisible(p => ({ ...p, [icao]: true }));
            const res = await fetch(`${API_BASE}/circling/assess`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ aerodrome: icao, obstacles: obsWithCoords })
            });
            const data = await res.json();
            setCirResults(p => ({ ...p, [icao]: { loading: false, data, error: data.error } }));
        } catch (err) {
            setCirResults(p => ({ ...p, [icao]: { loading: false, error: err.message } }));
        }
    };

    const [preview3DIcao, setPreview3DIcao] = useState(null);

    const handleDownloadKmz = async (icao, mode) => {
        try {
            const url = `${API_BASE}/circling/${icao}/kml?mode=${mode}${project?.id ? `&project_id=${project.id}` : ''}`;
            const response = await fetch(url);
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Failed to download KMZ: ${text}`);
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${icao}_CIRCLING_${mode.toUpperCase()}.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Circling KMZ export error:', err);
            alert("Failed to export KMZ: " + err.message);
        }
    };

    const resetAssessment = (icao) => {
        setCirResults(p => { const next = {...p}; delete next[icao]; return next; });
        setVisible(p => ({...p, [icao]: false }));
    };

    const renderResultsContent = () => {
        if (!activeIcao) {
            return (
                <div style={{ padding: 30, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                    Click <strong style={{color: T.blue}}>▶ Assess</strong> on an aerodrome, then 📋 to view or hide its results.
                </div>
            );
        }
        const icao = activeIcao;
        const res = cirResults[icao];
        if (!res) return null;

        // Mark the controlling obstacle (worst true clearance) per circling category.
        // total_elev_ft - category_minima_ft is recomputed here because the backend's
        // penetration_ft/penetration_m are zeroed out for non-penetrating evaluations,
        // which would hide the worst-but-still-clear obstacle.
        if (res.data?.results?.length) {
            const worstByCategory = new Map();
            res.data.results.forEach(r => {
                r.evaluations.forEach(ev => {
                    const trueP = ev.total_elev_ft - ev.category_minima_ft;
                    const cur = worstByCategory.get(ev.category);
                    if (!cur || trueP > cur.trueP) worstByCategory.set(ev.category, { ev, trueP });
                });
            });
            worstByCategory.forEach(({ ev }) => { ev.isControlling = true; });
        }

        return (
            <div key={icao} style={{ padding: 20, color: T.white }}>
                {res.loading ? (
                    <div style={{ color: T.muted }}>Assessing {icao}...</div>
                ) : res.error ? (
                    <div style={{ color: '#e84040' }}>No circling boundary derived for {icao}: {res.error}</div>
                ) : res.data && res.data.results ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#f39c12' }}>{res.data.aerodrome} Circling Area Assessment</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button
                                    onClick={() => {
                                        const headers = ["Category", "#", "Obstacle", "Elevation (m)", "MOC (ft)", "V Tol (m)", "Total Elevation (ft)", "Minima (ft)", "Result", "Controlling"];
                                        const rows = [];
                                        ['A', 'B', 'C', 'D'].forEach(cat => {
                                            const rowsForCat = res.data.results.flatMap(r => r.evaluations.filter(ev => ev.category === cat).map(ev => ({ ev, r })));
                                            rowsForCat.forEach(({ ev, r }, idx) => {
                                                const obsRecord = obstacles.find(o => o.id === r.obstacle_id);
                                                rows.push([
                                                    cat,
                                                    idx + 1,
                                                    r.obstacle_name,
                                                    r.obstacle_elev_m.toFixed(1),
                                                    ev.moc_applied_ft,
                                                    obsRecord?.v_tolerance ?? 0,
                                                    ev.total_elev_ft,
                                                    ev.category_minima_ft,
                                                    ev.penetrates ? "IMPACT" : "NO IMPACT",
                                                    ev.isControlling ? 'Yes' : ''
                                                ]);
                                            });
                                        });
                                        exportToCsv(headers, rows, `${res.data.aerodrome}_Circling_Assessment.csv`);
                                    }}
                                    style={{
                                        padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                        background: T.blue, color: T.white, fontSize: 11, fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: 4
                                    }}
                                >
                                    📥 Export CSV
                                </button>
                                <button
                                    onClick={() => handleDownloadKmz(res.data.aerodrome, '2d')}
                                    title="Download 2D KMZ (Flat Footprints)"
                                    style={{
                                        background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                                        borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
                                        color: T.muted, fontSize: 11, fontWeight: 700
                                    }}
                                >
                                    KMZ (2D)
                                </button>
                                <button
                                    onClick={() => handleDownloadKmz(res.data.aerodrome, '3d')}
                                    title="Download 3D KMZ (True Altitudes)"
                                    style={{
                                        background: 'rgba(58,138,196,0.15)', border: `1px solid ${T.blue}44`,
                                        borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
                                        color: T.blue, fontSize: 11, fontWeight: 700
                                    }}
                                >
                                    KMZ (3D)
                                </button>
                                <button
                                    onClick={() => setPreview3DIcao(res.data.aerodrome)}
                                    title="Preview the Circling areas and project obstacles in 3D, right here"
                                    style={{
                                        background: 'rgba(46,204,113,0.15)', border: '1px solid #2ECC7144',
                                        borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
                                        color: '#2ECC71', fontSize: 11, fontWeight: 700
                                    }}
                                >
                                    🌐 3D Preview
                                </button>
                            </div>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: T.bg0 }}>
                            <thead>
                                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Aerodrome</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>ICAO DESIG</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>AD Elev (m)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ borderBottom: `1px solid ${T.border}33` }}>
                                    <td style={{ padding: 10 }}>{validArps.find(a=>a.icao===icao)?.name || icao}</td>
                                    <td style={{ padding: 10, fontWeight: 'bold' }}>{res.data.aerodrome}</td>
                                    <td style={{ padding: 10 }}>{res.data.ad_elev_m} m</td>
                                </tr>
                            </tbody>
                        </table>

                        {res.data.results.length === 0 ? (
                            <div style={{ marginTop: 16, padding: 16, background: T.bg0, borderRadius: 6, borderLeft: `4px solid #2ec98b` }}>
                                No obstacles intersect the Circling Area for {icao}.
                            </div>
                        ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                            <thead>
                                <tr style={{ background: T.bg0, borderBottom: `2px solid ${T.border}` }}>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted, width: 30 }}>#</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Obstacle / Elevation</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>MOC (ft)</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>V Tol (m)</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Total Elevation (ft)</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Minima (ft)</th>
                                    <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['A', 'B', 'C', 'D'].filter(cat => catVisible[icao]?.[cat] !== false).map(cat => {
                                    const rowsForCat = res.data.results.flatMap(r =>
                                        r.evaluations.filter(ev => ev.category === cat).map(ev => ({ ev, r }))
                                    );
                                    return (
                                        <React.Fragment key={cat}>
                                            <tr style={{ background: 'rgba(243,156,18,0.12)' }}>
                                                <td colSpan={7} style={{ padding: '8px 10px', fontWeight: 800, color: '#f39c12' }}>CAT {cat}</td>
                                            </tr>
                                            {rowsForCat.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} style={{ padding: '8px 10px', color: T.muted, fontStyle: 'italic' }}>No obstacles in CAT {cat}</td>
                                                </tr>
                                            ) : rowsForCat.map(({ ev, r }, idx) => {
                                                const obsRecord = obstacles.find(o => o.id === r.obstacle_id);
                                                const vTol = obsRecord?.v_tolerance ?? 0;
                                                return (
                                                    <tr key={idx} style={{ borderBottom: `1px solid ${T.border}33`, background: ev.penetrates ? 'rgba(232,64,64,0.08)' : 'transparent' }}>
                                                        <td style={{ padding: 10, color: T.muted }}>{idx + 1}</td>
                                                        <td style={{ padding: 10, fontWeight: 'bold' }}>
                                                            {r.obstacle_name} ({r.obstacle_elev_m.toFixed(1)} m)
                                                            {ev.isControlling && <span style={{ marginLeft: 6, color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                                        </td>
                                                        <td style={{ padding: 10 }}>+ {ev.moc_applied_ft}</td>
                                                        <td style={{ padding: 10 }}>{vTol}</td>
                                                        <td style={{ padding: 10, fontWeight: 'bold' }}>{ev.total_elev_ft}</td>
                                                        <td style={{ padding: 10, fontWeight: 'bold' }}>{ev.category_minima_ft}</td>
                                                        <td style={{ padding: 10, fontWeight: 'bold', color: ev.penetrates ? '#e84040' : '#2ec98b' }}>
                                                            {ev.penetrates ? "IMPACT" : "NO IMPACT"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                        )}
                    </div>
                ) : (
                    <div style={{ color: T.muted }}>Unable to assess {icao}.</div>
                )}
            </div>
        );
    };

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=circling`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Server resolves the real aerodrome(s) from obstacle proximity and
            // builds the filename itself — read it back rather than
            // reconstructing one from project.aerodrome (can be stale).
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_CIRCLING_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    return (
        <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <div style={{ width: 280, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Target Obstacle
                        </div>
                        <select
                            value={selObstId || ''}
                            onChange={(e) => setSelId(e.target.value)}
                            style={{ width: '100%', background: T.bg0, border: `1px solid ${T.border}`, color: T.white, padding: '6px 8px', borderRadius: 4, fontSize: 13, outline: 'none' }}
                        >
                            {obsWithCoords.map(o => <option key={o.id} value={o.id}>{o.name || 'Unnamed'}</option>)}
                        </select>
                        <button
                            onClick={handleExportAixm}
                            disabled={exportingAixm}
                            style={{ marginTop: 10, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                            title="Export obstacles, aerodrome data, waypoints and Circling areas (CirclingArea) as AIXM 5.1"
                        >
                            {exportingAixm ? '⏳…' : '📤 AIXM'}
                        </button>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <div style={{ padding: '12px 16px', fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', background: `rgba(255,255,255,0.02)` }}>
                            Aerodromes ({validArps.length})
                        </div>
                        {validArps.length === 0 && <div style={{ padding: 16, color: T.muted, fontSize: 12, fontStyle: 'italic' }}>No aerodromes available.</div>}
                        {validArps.map((a, i) => {
                            const isAssessed = !!cirResults[a.icao]?.data;
                            const isAssessingNow = cirResults[a.icao]?.loading;
                            const isOn = visible[a.icao] !== false;
                            
                            return (
                                <div key={i} style={{ borderBottom: `1px solid ${T.border}22`, padding: '8px 12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f39c12', flexShrink: 0 }} />
                                        <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.white }}>{a.icao}</span>
                                        
                                        {!validIcaos.includes(a.icao?.toUpperCase()) ? (
                                            <div style={{ fontSize: 9, color: T.danger, background: T.danger + '11', padding: '2px 6px', borderRadius: 4, border: `1px solid ${T.danger}33` }}>
                                                ⚠️ No Minima
                                            </div>
                                        ) : (
                                            <>
                                                {/* Screenshot button */}
                                                {isAssessed && (
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <button title="Capture map screenshot for report" onClick={() => captureCirclingScreenshot(a.icao)} style={{
                                                            background: capturedCircling[a.icao] ? T.safe + '22' : 'rgba(255,255,255,0.07)',
                                                            border: `1px solid ${capturedCircling[a.icao] ? T.safe : T.border}`,
                                                            color: capturedCircling[a.icao] ? T.safe : T.muted,
                                                            borderRadius: 4, padding: '2px 5px', cursor: 'pointer', fontSize: 12,
                                                        }}>{capturedCircling[a.icao] ? '✓ Captured' : 'Capture Screenshot'}</button>
                                                        {capturedCircling[a.icao] && (
                                                            <button title="Clear screenshot" onClick={() => clearCirclingScreenshot(a.icao)} style={{
                                                                background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`,
                                                                color: T.danger, borderRadius: 4, padding: '2px 5px', cursor: 'pointer', fontSize: 10,
                                                            }}>✕</button>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {/* Assess / Results buttons */}
                                                <button onClick={() => runAssessment(a.icao)} disabled={isAssessingNow} style={{
                                                    padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10,
                                                    background: isAssessed ? T.blue + '33' : T.blue,
                                                    color: isAssessed ? T.blue : '#fff', fontWeight: 700,
                                                }}>{isAssessingNow ? '…' : isAssessed ? '↻' : '▶ Assess'}</button>
                                                
                                                {isAssessed && (
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <button title={activeIcao === a.icao ? "Hide Results" : "Show Results"} onClick={() => setActiveIcao(activeIcao === a.icao ? null : a.icao)} style={{
                                                            padding: '2px 6px', borderRadius: 4, border: `1px solid ${T.border}`,
                                                            background: activeIcao === a.icao ? T.blue + '33' : 'transparent',
                                                            color: activeIcao === a.icao ? T.blue : T.muted, cursor: 'pointer', fontSize: 12,
                                                        }}>📋</button>
                                                        <button title="Reset Assessment (remove from report)" onClick={() => resetAssessment(a.icao)} style={{
                                                            padding: '2px 6px', borderRadius: 4, border: `1px solid ${T.border}`,
                                                            background: 'transparent', color: T.danger, cursor: 'pointer', fontSize: 12,
                                                        }}>🗑️</button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 10, color: T.muted, marginBottom: 2 }}>{a.name}</div>
                                    {!validIcaos.includes(a.icao?.toUpperCase()) && (
                                        <div style={{ fontSize: 9, color: T.muted, fontStyle: 'italic', marginBottom: 4 }}>
                                            No published circling minima found for this aerodrome. Areas cannot be constructed.
                                        </div>
                                    )}
                                    
                                    {isAssessed && (
                                        <div style={{ paddingLeft: 16, fontSize: 10, color: T.muted }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={isOn} onChange={e => setVisible(p => ({ ...p, [a.icao]: e.target.checked }))} />
                                                <span style={{ fontWeight: 700, color: isOn ? T.white : T.muted }}>All surfaces</span>
                                            </label>
                                            {cirResults[a.icao]?.data?.polygons && (
                                                <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', paddingLeft: 10 }}>
                                                    {cirResults[a.icao].data.polygons.features.map(f => f.properties.category).sort().map(cat => {
                                                        const isCatOn = catVisible[a.icao]?.[cat] !== false;
                                                        return (
                                                            <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={isCatOn} 
                                                                    onChange={() => setCatVisible(prev => ({
                                                                        ...prev, 
                                                                        [a.icao]: { ...(prev[a.icao] || {}), [cat]: !isCatOn }
                                                                    }))} 
                                                                    style={{ accentColor: '#2ec98b' }}
                                                                />
                                                                {cat}
                                                            </label>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, position: 'relative', background: '#0a192f' }}>
                        {loading && <div style={{ position: 'absolute', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.5)', color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>Loading Map…</div>}
                        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', alignItems: 'center' }}>
                            {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                                <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                            ))}
                        </div>
                        <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                            penetratingCount={circlingShieldRealPenetration.size}
                            onExportPenetrating={() => {
                                const byId = new Map();
                                [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                                 ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                                    byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                                });
                                const entries = [];
                                circlingShieldRealPenetration.forEach((penList, obsId) => {
                                    const r = byId.get(obsId);
                                    if (!r) return;
                                    penList.forEach(v => entries.push({
                                        r,
                                        surfaceLabel: `Circling Cat ${v.category}`,
                                        penetrationAmount: v.margin.toFixed(2),
                                    }));
                                });
                                exportSurfacePenetratingCsv(entries, radiusKm);
                            }} />
                        <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : (activeIcao ? 220 : 36)} />
                        <MapCoordinateViewer map={mapState} />
                        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                    </div>

                    <div style={{ height: resultsCollapsed ? 36 : (activeIcao ? resultsHeight : 36), background: T.bg1, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden' }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🔄</span> Visual Manoeuvring (Circling) Area Report
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                {activeIcao && !resultsPoppedOut && (
                                    <button
                                        onClick={() => {
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        fontWeight: 600
                                    }}
                                >
                                    {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                                </button>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out. 
                                    <button 
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : renderResultsContent()}
                        </div>
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - Circling Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={900}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}

            {preview3DIcao && (
                <Cesium3DPreviewModal
                    title={`${preview3DIcao} — Circling 3D Preview`}
                    kmlUrl={`${API_BASE}/circling/${preview3DIcao}/kml?mode=3d${project?.id ? `&project_id=${project.id}` : ''}`}
                    onClose={() => setPreview3DIcao(null)}
                />
            )}
        </div>
    );
}
